import { useSearchParams, useNavigate } from 'react-router-dom';
import { useEffect, useCallback, useRef, useState } from 'react';
import { useSignature } from './useSignature';
import { request } from '../utils/api';
import { getSessionTenantId } from '../utils/tenantContext';
import { isFormReadOnly } from '../utils/formPermissions';
import { resolveApplicationFormClose } from '../utils/applicationFormNavigation';
import { useSubmitLock } from './useSubmitLock';

/**
 * Custom hook to handle common application form logic:
 * - Wraps useSignature for signature modal management
 * - Extracting targetUserId from search params
 * - Initializing userId and tenantId from current user
 * - Auto-filling applicant/employee name if it's the applicant filling the form
 * - Handling signature results and mapping them to form fields
 * 
 * @param {Object} options Configuration options
 * @param {Object} options.methods React Hook Form methods
 * @param {string} options.applicantNameField Name of the field for applicant's full name
 * @param {string} options.applicantSignatureField Name of the field for applicant's signature
 * @param {string} options.applicantSignatureDateField Name of the field for applicant's signature date
 * @param {string} options.adminSignatureField Name of the field for admin's signature
 * @param {string} options.adminSignatureDateField Name of the field for admin's signature date
 */
export const useApplicationFormLogic = ({
    methods,
    applicantNameField = "",
    applicantSignatureField = "applicantSignature",
    applicantSignatureDateField = "applicantSignedAt",
    adminSignatureField = "reviewedBySignature",
    adminSignatureDateField = "reviewedBySignedAt",
    adminNameField = "reviewedBySignedBy",
    apiPath = null,
    onApplicationLoaded = () => { },
    onClose = null,
    onSubmit = null,
    currentStatus = "",
    skipIdentityAutoFill = false,
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const capturedUserId = searchParams.get("userId");
    const actualFromId = searchParams.get("actualFromId");

    const { setValue, getValues } = methods;
    const [currentUser, setCurrentUser] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);

    // Use specific refs for callbacks to avoid dependency cycles
    const onApplicationLoadedRef = useRef(onApplicationLoaded);

    // Update ref when callback changes
    useEffect(() => {
        onApplicationLoadedRef.current = onApplicationLoaded;
    }, [onApplicationLoaded]);

    const [activeFields, setActiveFields] = useState(null);
    const { runLocked } = useSubmitLock();

    const {
        isSignatureModalOpen,
        openSignatureModal: internalOpenSignatureModal,
        closeSignatureModal,
        signerName,
        saveSignature,
    } = useSignature({});

    const openSignatureModal = useCallback((type, fieldOverrides = null) => {
        if (fieldOverrides) {
            setActiveFields(fieldOverrides);
        } else {
            setActiveFields(null);
        }
        internalOpenSignatureModal(type);
    }, [internalOpenSignatureModal]);

    // Initialize userId, tenantId and applicant name
    useEffect(() => {
        const userStr = localStorage.getItem("currentUser");
        if (userStr) {
            try {
                const user = JSON.parse(userStr);
                setCurrentUser(user);

                // Set tenantId. Owner (userId / employee name) can be skipped for forms
                // that pick an employee themselves (Daily Log).
                if (user.tenantId) {
                    setValue("tenantId", user.tenantId);
                } else {
                    const sessionTenantId = getSessionTenantId();
                    if (sessionTenantId) setValue("tenantId", sessionTenantId);
                }

                const roleName = (typeof user.role === 'object' ? user.role?.name : user.role) || '';
                const admin = roleName.toLowerCase() === 'admin' || user.roleId === 1 || user.role?.id === 1;
                setIsAdmin(admin);

                if (skipIdentityAutoFill) {
                    return;
                }

                if (capturedUserId) {
                    setValue("userId", capturedUserId);
                } else if (!getValues("userId")) {
                    const id = user.id || user.userId;
                    if (id) setValue("userId", id);
                }

                if (!capturedUserId && !admin && applicantNameField && !getValues(applicantNameField)) {
                    if (user.firstName && user.lastName) {
                        setValue(applicantNameField, `${user.firstName} ${user.lastName}`);
                    } else if (user.name) {
                        setValue(applicantNameField, user.name);
                    }
                }

            } catch (e) {
                console.error("Failed to parse user in useApplicationFormLogic", e);
            }
        }
    }, [setValue, capturedUserId, applicantNameField, getValues, skipIdentityAutoFill]);

    // Fetch user application data if capturedUserId and apiPath are provided
    useEffect(() => {
        const fetchApplicationData = async () => {
            if (capturedUserId && apiPath) {
                try {
                    const response = await request(`${apiPath}/${capturedUserId}`);
                    if (response.status === 1 && response.result?.application) {
                        if (onApplicationLoadedRef.current) {
                            onApplicationLoadedRef.current(response.result.application);
                        }
                    }
                } catch (error) {
                    console.error("Failed to fetch application data:", error);
                }
            }
        };

        fetchApplicationData();
    }, [capturedUserId, apiPath]);

    const handleSignatureApply = useCallback(() => {
        const result = saveSignature();
        if (result && result.formattedSignature) {
            const isAdm = result.signatureType === 'admin';

            // Determine which fields to use
            const sigField = activeFields?.signatureField || (isAdm ? adminSignatureField : applicantSignatureField);
            const dateFld = activeFields?.dateField || (isAdm ? adminSignatureDateField : applicantSignatureDateField);
            const nameFld = activeFields?.nameField || (isAdm ? adminNameField : applicantNameField);

            if (sigField) {
                setValue(sigField, result.formattedSignature, { shouldValidate: true });
            }

            if (nameFld && result.signerName) {
                setValue(nameFld, result.signerName);
            }

            if (dateFld) {
                setValue(dateFld, new Date().toISOString().split('T')[0], { shouldValidate: true });
            }

            closeSignatureModal();
            setActiveFields(null);
        }
    }, [saveSignature, closeSignatureModal, setValue, activeFields, applicantNameField, applicantSignatureField, applicantSignatureDateField, adminSignatureField, adminSignatureDateField, adminNameField]);

    // Read-only logic centralized in utility
    const isReadOnly = isFormReadOnly(currentUser, currentStatus, capturedUserId);

    const closeForm = useCallback(() => {
        resolveApplicationFormClose({
            navigate,
            searchParams,
            capturedUserId,
            onClose,
            setSearchParams,
        });
    }, [navigate, capturedUserId, searchParams, onClose, setSearchParams]);

    const handleSave = useCallback(async (data, explicitFlag) => {
        if (typeof onSubmit !== 'function') return;
        const flag = explicitFlag || ((currentStatus === 'pending' || !currentStatus) ? 'save' : 'send_to_office');
        await runLocked(flag, () => onSubmit(data, flag));
    }, [onSubmit, currentStatus, runLocked]);

    const handleStatusAction = useCallback(async (status, additionalData) => {
        if (typeof onSubmit !== 'function') return;
        await runLocked(status, () =>
            methods.handleSubmit((data) => onSubmit(data, status, additionalData))()
        );
    }, [methods, onSubmit, runLocked]);

    return {
        targetUserId: capturedUserId,
        actualFromId,
        isSignatureModalOpen,
        openSignatureModal,
        closeSignatureModal,
        signerName,
        handleSignatureApply,
        saveSignature,
        user: currentUser,
        isAdmin,
        isReadOnly,
        closeForm,
        handleSave,
        handleStatusAction,
        navigate
    };
};
